/*
Plain-text renderings of field values, for the "not saved" notices, the cue and
the leave dialogs (XOL-596, UX spec C-2 and "User text in notices and cues").

Never render user HTML: remarks become plain text through DOMParser after the
block ends are turned into newlines. No `innerHTML` on a live element, no
`dangerouslyUseHTMLString`. ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import { copyText } from '@/lib/requestSyncCopy'

/** HTML -> plain text, keeping line breaks. */
export function plainText (html) {
  if (html === undefined || html === null) {
    return ''
  }
  var s = String(html).replace(/<\/p>|<br\s*\/?>|<\/li>/gi, '\n')
  var text = s
  try {
    var doc = new window.DOMParser().parseFromString(s, 'text/html')
    text = doc.body ? doc.body.textContent : s
  } catch (e) {
    text = s.replace(/<[^>]*>/g, '')
  }
  return text.replace(/\n+$/, '')
}

var HTML_PATHS = ['remarks', 'resubmitRemarks']

function lastSegment (path) {
  var parts = String(path).split('.')
  return parts[parts.length - 1]
}

/** 'text' (a remarks editor), 'list' (an array) or 'scalar'. */
export function valueKind (path, value) {
  if (HTML_PATHS.indexOf(lastSegment(path)) !== -1) {
    return 'text'
  }
  if (Array.isArray(value)) {
    return 'list'
  }
  return 'scalar'
}

function isEmpty (v) {
  return v === undefined || v === null || v === ''
}

/** One list entry as a line: an e-mail address, or `{qty} × {name}`. */
export function describeEntry (entry) {
  if (entry && typeof entry === 'object') {
    if (entry.email) {
      return entry.email
    }
    var name = entry.name || entry.type || ''
    var line = (entry.qty !== undefined && entry.qty !== null ? entry.qty + ' × ' : '') + name
    if (entry.setNo) {
      line += ' (Set ' + entry.setNo + ')'
    }
    return line
  }
  return String(entry)
}

/**
 * A scalar as the user reads it. `fmt` = { date, datetime } (the caller's
 * formatDate / formatDateTime).
 */
export function describeScalar (path, value, fmt) {
  // The decision paths (XOL-596): a status of null or '' IS Pending, the date is a
  // calendar day (never the raw DATETIME), the legal tick is yes / no.
  if (path === 'approvedStatus') {
    return isEmpty(value) ? 'Pending' : String(value)
  }
  if (path === 'approved_at' && !isEmpty(value)) {
    return fmt && fmt.date ? fmt.date(value) : String(value)
  }
  if (path === 'state.step3.legalApproved') {
    return value === true || value === 1 || value === '1' || value === 'true'
      ? copyText('requestSync.notice.yes')
      : copyText('requestSync.notice.no')
  }
  if (isEmpty(value)) {
    return copyText('requestSync.notice.empty')
  }
  if (value === true || value === 'true') {
    return copyText('requestSync.notice.yes')
  }
  if (value === false) {
    return copyText('requestSync.notice.no')
  }
  var last = lastSegment(path)
  if (fmt && (last === 'targetDate' || last === 'actualDate' || /^approved(PTW|CME|TI|MW)$/.test(last))) {
    return fmt.date ? fmt.date(value) : String(value)
  }
  return String(value)
}

/**
 * The entries of `a` that are not in `b` (by e-mail, else by JSON), as lines.
 */
export function listDifference (a, b) {
  var key = function (e) {
    return e && typeof e === 'object' && e.email ? e.email : JSON.stringify(e)
  }
  var other = (b || []).map(key)
  return (a || []).filter(function (e) {
    return other.indexOf(key(e)) === -1
  }).map(describeEntry)
}
